// ============================================================================
// BP-04-02: Documents Frontend - useDocumentSearch Hook
// ============================================================================

import { useState, useCallback, useMemo, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useDebounce } from '@/core/hooks/useDebounce';
import { documentsApi } from '../api/documentsApi';
import { useDocumentsStore } from '../stores/documentsStore';
import type {
  DocumentSearchParams,
  DocumentSearchResult,
  DocumentFilters,
  DocumentType,
  SortField,
  SortOrder,
} from '../types/documentsTypes';

// -----------------------------------------------------------------------------
// Query Keys
// -----------------------------------------------------------------------------

export const searchKeys = {
  all: ['document-search'] as const,
  search: (familyId: string, params: DocumentSearchParams) =>
    [...searchKeys.all, familyId, params] as const,
  suggestions: (familyId: string, query: string) =>
    [...searchKeys.all, 'suggestions', familyId, query] as const,
};

// -----------------------------------------------------------------------------
// Types
// -----------------------------------------------------------------------------

interface UseDocumentSearchOptions {
  familyId: string;
  enabled?: boolean;
  debounceMs?: number;
  minQueryLength?: number;
}

interface UseDocumentSearchReturn {
  // Search state
  query: string;
  setQuery: (query: string) => void;
  debouncedQuery: string;
  
  // Filters
  filters: DocumentFilters;
  setFilters: (filters: Partial<DocumentFilters>) => void;
  clearFilters: () => void;
  activeFilterCount: number;
  
  // Sorting
  sortBy: SortField;
  sortOrder: SortOrder;
  setSortBy: (field: SortField) => void;
  setSortOrder: (order: SortOrder) => void;
  toggleSortOrder: () => void;
  
  // Pagination
  page: number;
  setPage: (page: number) => void;
  perPage: number;
  setPerPage: (perPage: number) => void;
  
  // Results
  results: DocumentSearchResult | undefined;
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  
  // Actions
  search: () => void;
  reset: () => void;
  
  // Suggestions (for autocomplete)
  suggestions: string[];
  isLoadingSuggestions: boolean;
}

// -----------------------------------------------------------------------------
// Hook
// -----------------------------------------------------------------------------

export function useDocumentSearch({
  familyId,
  enabled = true,
  debounceMs = 300,
  minQueryLength = 2,
}: UseDocumentSearchOptions): UseDocumentSearchReturn {
  const queryClient = useQueryClient();
  const { filters: storeFilters, sortBy, sortOrder, setFilters: setStoreFilters } = useDocumentsStore();

  // Local state
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(20);
  const [localFilters, setLocalFilters] = useState<DocumentFilters>({});

  // Debounce search query
  const debouncedQuery = useDebounce(query, debounceMs);

  // Merge filters
  const mergedFilters: DocumentFilters = useMemo(
    () => ({
      ...storeFilters,
      ...localFilters,
      search: debouncedQuery || undefined,
    }),
    [storeFilters, localFilters, debouncedQuery]
  );

  // Build search params
  const searchParams: DocumentSearchParams = useMemo(
    () => ({
      ...mergedFilters,
      page,
      per_page: perPage,
      sort_by: sortBy,
      sort_order: sortOrder,
    }),
    [mergedFilters, page, perPage, sortBy, sortOrder]
  );

  // Count active filters
  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (mergedFilters.document_type) count++;
    if (mergedFilters.child_id) count++;
    if (mergedFilters.date_from || mergedFilters.date_to) count++;
    if (mergedFilters.tags?.length) count++;
    if (mergedFilters.is_shared_with_attorney !== undefined) count++;
    return count;
  }, [mergedFilters]);

  // Determine if search should be enabled
  const shouldSearch =
    enabled &&
    !!familyId &&
    (debouncedQuery.length >= minQueryLength ||
      debouncedQuery.length === 0 ||
      activeFilterCount > 0);

  // Search query
  const {
    data: results,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: searchKeys.search(familyId, searchParams),
    queryFn: () => documentsApi.search(familyId, searchParams),
    enabled: shouldSearch,
    staleTime: 30 * 1000,
    placeholderData: (previousData) => previousData,
  });

  // Suggestions query (for autocomplete)
  const { data: suggestionsData, isLoading: isLoadingSuggestions } = useQuery({
    queryKey: searchKeys.suggestions(familyId, debouncedQuery),
    queryFn: async () => {
      // This would call a suggestions endpoint
      // For now, return empty array
      return { suggestions: [] as string[] };
    },
    enabled: enabled && !!familyId && debouncedQuery.length >= 2,
    staleTime: 60 * 1000,
  });

  // Reset page when filters change
  useEffect(() => {
    setPage(1);
  }, [debouncedQuery, localFilters, sortBy, sortOrder]);

  // Actions
  const setFilters = useCallback(
    (filters: Partial<DocumentFilters>) => {
      setLocalFilters((prev) => ({ ...prev, ...filters }));
    },
    []
  );

  const clearFilters = useCallback(() => {
    setLocalFilters({});
    setStoreFilters({});
    setQuery('');
  }, [setStoreFilters]);

  const setSortBy = useCallback(
    (field: SortField) => {
      useDocumentsStore.getState().setSortBy(field);
    },
    []
  );

  const setSortOrder = useCallback(
    (order: SortOrder) => {
      useDocumentsStore.getState().setSortOrder(order);
    },
    []
  );

  const toggleSortOrder = useCallback(() => {
    useDocumentsStore.getState().toggleSortOrder();
  }, []);

  const search = useCallback(() => {
    refetch();
  }, [refetch]);

  const reset = useCallback(() => {
    setQuery('');
    setLocalFilters({});
    setPage(1);
    setPerPage(20);
  }, []);

  return {
    // Search state
    query,
    setQuery,
    debouncedQuery,
    
    // Filters
    filters: mergedFilters,
    setFilters,
    clearFilters,
    activeFilterCount,
    
    // Sorting
    sortBy,
    sortOrder,
    setSortBy,
    setSortOrder,
    toggleSortOrder,
    
    // Pagination
    page,
    setPage,
    perPage,
    setPerPage,
    
    // Results
    results,
    isLoading,
    isError,
    error: error as Error | null,
    
    // Actions
    search,
    reset,
    
    // Suggestions
    suggestions: suggestionsData?.suggestions || [],
    isLoadingSuggestions,
  };
}

// -----------------------------------------------------------------------------
// Search Filter Presets
// -----------------------------------------------------------------------------

export interface SearchPreset {
  id: string;
  name: string;
  icon: string;
  filters: DocumentFilters;
}

export const SEARCH_PRESETS: SearchPreset[] = [
  {
    id: 'recent',
    name: 'Recent',
    icon: 'Clock',
    filters: {},
  },
  {
    id: 'medical',
    name: 'Medical Records',
    icon: 'Stethoscope',
    filters: { document_type: 'medical_record' as DocumentType },
  },
  {
    id: 'iep',
    name: 'IEP Documents',
    icon: 'GraduationCap',
    filters: { document_type: 'iep' as DocumentType },
  },
  {
    id: 'legal',
    name: 'Legal Documents',
    icon: 'Scale',
    filters: { document_type: 'legal' as DocumentType },
  },
  {
    id: 'insurance',
    name: 'Insurance',
    icon: 'Shield',
    filters: { document_type: 'insurance' as DocumentType },
  },
  {
    id: 'shared',
    name: 'Shared with Attorney',
    icon: 'Share2',
    filters: { is_shared_with_attorney: true },
  },
];

// -----------------------------------------------------------------------------
// Export
// -----------------------------------------------------------------------------


export default useDocumentSearch;




